import { HttpClient } from '@angular/common/http';
import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { mockNotificationsForRole } from 'core/mocks/notifications.mock';
import {
  Notification,
  NotificationPreference,
} from 'core/models/notification.model';
import { AuthSessionService } from 'core/services/auth-session.service';
import { ConfirmDialogService } from 'core/services/confirm-dialog.service';
import { NotificationTone } from 'shared/utils/notification-tone.util';
import { defaultNotificationPreferences } from 'core/constants/notification-preferences';
import { EMPTY, Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { bulkTrashConfirmMessage } from 'shared/utils/bulk-list-messages.util';

interface NotificationsApiResponse {
  ok: boolean;
  message: string;
  data: NotificationApiItem[];
}

interface NotificationItemApiResponse {
  ok: boolean;
  message: string;
  data: NotificationApiItem;
}

interface UnreadCountApiResponse {
  ok: boolean;
  message: string;
  count: number;
}

interface PreferencesApiResponse {
  ok: boolean;
  message: string;
  data: NotificationPreferenceApiItem[];
}

interface NotificationApiItem {
  id: number | string;
  user_id: number | string;
  type?: string | null;
  title: string;
  description: string;
  icon_class: string;
  tone: NotificationTone;
  highlight?: string | null;
  highlight_tone?: NotificationTone | null;
  action_url?: string | null;
  read: boolean;
  dismissed: boolean;
  dismissed_at?: string | null;
  created_at: string;
}

interface NotificationPreferenceApiItem {
  notification_type: string;
  muted: boolean;
}

const POLL_INTERVAL_MS = 60_000;
const PENDING_UNREAD_STORAGE_KEY = 'oneeduca.pendingUnreadNotificationIds';
const RESTORED_VISIBLE_STORAGE_KEY = 'oneeduca.restoredVisibleNotificationIds';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthSessionService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly items = signal<Notification[]>([]);
  private readonly trashedItems = signal<Notification[]>([]);
  private readonly unreadCountValue = signal(0);
  /** Mantém não lida no cliente quando a API ainda não expõe PATCH /unread. */
  private readonly pendingUnreadIds = signal<ReadonlySet<string>>(
    this.restorePendingUnreadFromStorage(),
  );
  /** Itens restaurados da lixeira permanecem visíveis mesmo se o tipo estiver silenciado. */
  private readonly restoredVisibleIds = signal<ReadonlySet<string>>(
    this.readIdSetFromStorage(RESTORED_VISIBLE_STORAGE_KEY),
  );
  private markUnreadApiUnavailable = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly trashLoading = signal(false);
  readonly trashLoadError = signal<string | null>(null);
  readonly preferences = signal<NotificationPreference[]>([]);
  readonly preferencesLoading = signal(false);

  readonly all = computed(() => this.sortedVisible());
  readonly trashed = computed(() => this.sortedTrashed());
  /** Dropdown do sino: apenas não lidas (as lidas ficam em /notifications até dispensar). */
  readonly recent = computed(() =>
    this.sortedVisible()
      .filter((item) => !item.read)
      .slice(0, 5),
  );
  readonly unreadCount = computed(
    () => this.sortedVisible().filter((item) => !item.read).length,
  );
  readonly mutedTypes = computed(
    () =>
      new Set(
        this.preferences()
          .filter((item) => item.muted)
          .map((item) => item.notificationType),
      ),
  );

  constructor() {
    effect(() => {
      const userId = this.auth.user()?.id;
      const role = this.auth.role();

      if (!userId || !this.auth.isAuthenticated()) {
        this.items.set([]);
        this.trashedItems.set([]);
        this.unreadCountValue.set(0);
        this.preferences.set([]);
        this.pendingUnreadIds.set(new Set());
        this.restoredVisibleIds.set(new Set());
        this.markUnreadApiUnavailable = false;
        this.clearPendingUnreadStorage();
        this.clearRestoredVisibleStorage();
        this.stopPolling();
        return;
      }

      if (environment.useMockAuth) {
        this.items.set(mockNotificationsForRole(role, userId));
        this.trashedItems.set([]);
        this.stopPolling();
        return;
      }

      this.loadPreferences();
      this.load();
      this.startPolling();
    });
  }

  load(): void {
    if (environment.useMockAuth || !this.auth.isAuthenticated()) {
      return;
    }

    this.loading.set(true);
    this.loadError.set(null);

    this.http
      .get<NotificationsApiResponse>(`${environment.apiUrl}/notifications`)
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => this.mapNotification(item)),
        ),
        tap(() => this.loading.set(false)),
        catchError((error: unknown) => {
          this.loading.set(false);
          this.loadError.set(this.resolveErrorMessage(error));
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.items.set(this.mergePendingUnread(items));
      });

    this.refreshUnreadCount();
  }

  loadPreferences(): void {
    if (!this.auth.isAuthenticated()) {
      this.preferences.set([]);
      return;
    }

    this.preferencesLoading.set(true);

    this.http
      .get<PreferencesApiResponse>(
        `${environment.apiUrl}/notifications/preferences`,
      )
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => this.mapPreference(item)),
        ),
        tap(() => this.preferencesLoading.set(false)),
        catchError(() => {
          this.preferencesLoading.set(false);
          return of([] as NotificationPreference[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) =>
        this.preferences.set(this.normalizePreferences(items)),
      );
  }

  savePreferences(preferences: NotificationPreference[]): void {
    if (!this.auth.isAuthenticated()) {
      return;
    }

    this.preferencesLoading.set(true);

    this.http
      .patch<PreferencesApiResponse>(
        `${environment.apiUrl}/notifications/preferences`,
        {
          preferences: preferences.map((item) => ({
            notification_type: item.notificationType,
            muted: item.muted,
          })),
        },
      )
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => this.mapPreference(item)),
        ),
        tap(() => this.preferencesLoading.set(false)),
        catchError(() => {
          this.preferencesLoading.set(false);
          return of(preferences);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) =>
        this.preferences.set(this.normalizePreferences(items)),
      );
  }

  resetPreferences(): void {
    const defaults = defaultNotificationPreferences();
    this.savePreferences(defaults);
  }

  loadTrashed(): void {
    if (environment.useMockAuth) {
      this.trashLoading.set(false);
      this.trashLoadError.set(null);
      return;
    }

    if (!this.auth.isAuthenticated()) {
      return;
    }

    this.trashLoading.set(true);
    this.trashLoadError.set(null);

    this.http
      .get<NotificationsApiResponse>(
        `${environment.apiUrl}/notifications/trashed`,
      )
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => this.mapNotification(item)),
        ),
        tap(() => this.trashLoading.set(false)),
        catchError((error: unknown) => {
          this.trashLoading.set(false);
          this.trashLoadError.set(this.resolveErrorMessage(error));
          return of([] as Notification[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => this.trashedItems.set(items));
  }

  restore(id: string): Observable<Notification | null> {
    const current = this.trashedItems().find(
      (notification) => notification.id === id,
    );
    if (!current) {
      return of(null);
    }

    const restored: Notification = {
      ...current,
      dismissed: false,
      dismissedAt: undefined,
    };

    if (environment.useMockAuth) {
      this.applyRestore(restored);
      return of(restored);
    }

    return this.http
      .post<NotificationItemApiResponse>(
        `${environment.apiUrl}/notifications/${id}/restore`,
        {},
      )
      .pipe(
        map((response) =>
          response.data ? this.mapNotification(response.data) : restored,
        ),
        tap((notification) => this.applyRestore(notification)),
        catchError(() => of(null)),
      );
  }

  forceDelete(id: string): void {
    this.unpinRestoredVisible(id);
    this.trashedItems.update((list) =>
      list.filter((notification) => notification.id !== id),
    );

    if (environment.useMockAuth) {
      return;
    }

    this.http
      .delete(`${environment.apiUrl}/notifications/${id}/force`)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  dismiss(id: string): void {
    const current = this.items().find((item) => item.id === id);
    const wasUnread = current?.read === false;

    if (environment.useMockAuth) {
      if (current) {
        this.moveToTrash(current);
      }
      return;
    }

    if (current) {
      this.moveToTrash(current);
    }
    this.removePendingUnread(id);
    if (wasUnread) {
      this.unreadCountValue.update((count) => Math.max(0, count - 1));
    }

    this.http
      .patch(`${environment.apiUrl}/notifications/${id}/dismiss`, {})
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshUnreadCount());
  }

  async confirmAndDismiss(id: string): Promise<boolean> {
    const item = this.all().find((notification) => notification.id === id);
    if (!item) {
      return false;
    }

    const confirmed = await this.confirmDialog.confirm({
      title: 'Enviar para a lixeira',
      message: bulkTrashConfirmMessage(1, 'notificação', 'notificações'),
      confirmText: 'Enviar',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (!confirmed) {
      return false;
    }

    this.dismiss(id);
    return true;
  }

  async confirmAndDismissMany(ids: readonly string[]): Promise<boolean> {
    const uniqueIds = [...new Set(ids)];
    if (uniqueIds.length === 0) {
      return false;
    }

    const confirmed = await this.confirmDialog.confirm({
      title: 'Enviar para a lixeira',
      message: bulkTrashConfirmMessage(
        uniqueIds.length,
        'notificação',
        'notificações',
      ),
      confirmText: 'Enviar',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (!confirmed) {
      return false;
    }

    this.dismissMany(uniqueIds);
    return true;
  }

  dismissMany(ids: readonly string[]): void {
    const idSet = new Set(ids);
    if (idSet.size === 0) {
      return;
    }

    const toTrash = this.items().filter((item) => idSet.has(item.id));
    for (const id of idSet) {
      this.removePendingUnread(id);
      this.unpinRestoredVisible(id);
    }

    this.items.update((list) => list.filter((item) => !idSet.has(item.id)));
    this.trashedItems.update((list) => [
      ...toTrash.map((item) => this.asTrashed(item)),
      ...list.filter((item) => !idSet.has(item.id)),
    ]);

    if (environment.useMockAuth) {
      return;
    }

    forkJoin(
      [...idSet].map((id) =>
        this.http
          .patch(`${environment.apiUrl}/notifications/${id}/dismiss`, {})
          .pipe(catchError(() => of(null))),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshUnreadCount());
  }

  markAllRead(): void {
    this.items.update((list) => list.map((item) => ({ ...item, read: true })));
    this.unreadCountValue.set(0);
    this.pendingUnreadIds.set(new Set());
    this.clearPendingUnreadStorage();

    if (environment.useMockAuth) {
      return;
    }

    this.http
      .patch(`${environment.apiUrl}/notifications/read-all`, {})
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshUnreadCount());
  }

  markUnread(id: string): void {
    const current = this.items().find((item) => item.id === id);
    if (!current?.read) {
      return;
    }

    this.items.update((list) =>
      list.map((item) => (item.id === id ? { ...item, read: false } : item)),
    );

    if (environment.useMockAuth) {
      return;
    }

    if (this.markUnreadApiUnavailable) {
      this.addPendingUnread(id);
      return;
    }

    this.http
      .patch(`${environment.apiUrl}/notifications/${id}/unread`, {})
      .pipe(
        tap(() => {
          this.markUnreadApiUnavailable = false;
          this.removePendingUnread(id);
          this.refreshUnreadCount();
        }),
        catchError((error) => {
          if (error?.status === 404) {
            this.markUnreadApiUnavailable = true;
            this.addPendingUnread(id);
            return of(null);
          }

          this.items.update((list) =>
            list.map((item) =>
              item.id === id ? { ...item, read: true } : item,
            ),
          );
          this.refreshUnreadCount();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async confirmAndDismissAll(): Promise<boolean> {
    if (this.all().length === 0) {
      return false;
    }

    const confirmed = await this.confirmDialog.confirm({
      title: 'Enviar para a lixeira',
      message: bulkTrashConfirmMessage(
        this.all().length,
        'notificação',
        'notificações',
      ),
      confirmText: 'Enviar',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (!confirmed) {
      return false;
    }

    this.dismissAll();
    return true;
  }

  dismissAll(): void {
    const ids = this.all().map((item) => item.id);
    const toTrash = this.items();

    for (const id of ids) {
      this.unpinRestoredVisible(id);
    }

    this.items.set([]);
    this.trashedItems.update((list) => [
      ...toTrash.map((item) => this.asTrashed(item)),
      ...list,
    ]);
    this.unreadCountValue.set(0);

    if (environment.useMockAuth) {
      return;
    }

    this.http
      .patch(`${environment.apiUrl}/notifications/dismiss-all`, {})
      .pipe(
        catchError((error) => {
          if (error?.status === 404 && ids.length > 0) {
            return forkJoin(
              ids.map((id) =>
                this.http
                  .patch(
                    `${environment.apiUrl}/notifications/${id}/dismiss`,
                    {},
                  )
                  .pipe(catchError(() => of(null))),
              ),
            );
          }

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.load());
  }

  markRead(id: string): void {
    this.items.update((list) =>
      list.map((item) => (item.id === id ? { ...item, read: true } : item)),
    );
    this.removePendingUnread(id);

    if (environment.useMockAuth) {
      return;
    }

    this.unreadCountValue.update((count) => Math.max(0, count - 1));

    this.http
      .patch(`${environment.apiUrl}/notifications/${id}/read`, {})
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshUnreadCount());
  }

  private mergePendingUnread(items: Notification[]): Notification[] {
    const pending = this.pendingUnreadIds();
    if (pending.size === 0) {
      return items;
    }

    return items.map((item) =>
      pending.has(item.id) && item.read ? { ...item, read: false } : item,
    );
  }

  private addPendingUnread(id: string): void {
    this.pendingUnreadIds.update((pending) => new Set([...pending, id]));
    this.persistPendingUnreadToStorage();
  }

  private removePendingUnread(id: string): void {
    this.pendingUnreadIds.update((pending) => {
      const next = new Set(pending);
      next.delete(id);
      return next;
    });
    this.persistPendingUnreadToStorage();
  }

  private restorePendingUnreadFromStorage(): ReadonlySet<string> {
    return this.readIdSetFromStorage(PENDING_UNREAD_STORAGE_KEY);
  }

  private persistPendingUnreadToStorage(): void {
    this.persistIdSetToStorage(
      PENDING_UNREAD_STORAGE_KEY,
      this.pendingUnreadIds(),
    );
  }

  private clearPendingUnreadStorage(): void {
    this.clearIdSetStorage(PENDING_UNREAD_STORAGE_KEY);
  }

  private pinRestoredVisible(id: string): void {
    this.restoredVisibleIds.update((ids) => new Set([...ids, id]));
    this.persistRestoredVisibleToStorage();
  }

  private unpinRestoredVisible(id: string): void {
    this.restoredVisibleIds.update((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
    this.persistRestoredVisibleToStorage();
  }

  private persistRestoredVisibleToStorage(): void {
    this.persistIdSetToStorage(
      RESTORED_VISIBLE_STORAGE_KEY,
      this.restoredVisibleIds(),
    );
  }

  private clearRestoredVisibleStorage(): void {
    this.clearIdSetStorage(RESTORED_VISIBLE_STORAGE_KEY);
  }

  private readIdSetFromStorage(key: string): ReadonlySet<string> {
    if (typeof sessionStorage === 'undefined') {
      return new Set();
    }

    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        return new Set();
      }

      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return new Set();
      }

      return new Set(
        parsed.filter((value): value is string => typeof value === 'string'),
      );
    } catch {
      return new Set();
    }
  }

  private persistIdSetToStorage(key: string, ids: ReadonlySet<string>): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const values = [...ids];
    if (values.length === 0) {
      sessionStorage.removeItem(key);
      return;
    }

    sessionStorage.setItem(key, JSON.stringify(values));
  }

  private clearIdSetStorage(key: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem(key);
  }

  private sortedTrashed(): Notification[] {
    return [...this.trashedItems()].sort((a, b) => {
      const aTime = new Date(a.dismissedAt ?? a.createdAt).getTime();
      const bTime = new Date(b.dismissedAt ?? b.createdAt).getTime();
      return bTime - aTime;
    });
  }

  private moveToTrash(item: Notification): void {
    this.unpinRestoredVisible(item.id);
    this.items.update((list) =>
      list.filter((notification) => notification.id !== item.id),
    );
    this.trashedItems.update((list) => [
      this.asTrashed(item),
      ...list.filter((notification) => notification.id !== item.id),
    ]);
  }

  private applyRestore(notification: Notification): void {
    const restored: Notification = {
      ...notification,
      dismissed: false,
      dismissedAt: undefined,
    };

    this.pinRestoredVisible(restored.id);
    this.trashedItems.update((list) =>
      list.filter((item) => item.id !== restored.id),
    );
    this.items.update((list) =>
      list.some((item) => item.id === restored.id)
        ? list.map((item) => (item.id === restored.id ? restored : item))
        : [restored, ...list],
    );
  }

  private asTrashed(item: Notification): Notification {
    return {
      ...item,
      read: true,
      dismissed: true,
      dismissedAt: new Date().toISOString(),
    };
  }

  private sortedVisible(): Notification[] {
    const muted = this.mutedTypes();
    const restoredVisible = this.restoredVisibleIds();

    return [...this.items()]
      .filter((item) => !item.dismissed)
      .filter(
        (item) =>
          !item.type || !muted.has(item.type) || restoredVisible.has(item.id),
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }

  private refreshUnreadCount(): void {
    if (environment.useMockAuth) {
      return;
    }

    this.http
      .get<UnreadCountApiResponse>(
        `${environment.apiUrl}/notifications/unread-count`,
      )
      .pipe(
        map((response) => response.count ?? 0),
        catchError(() => of(0)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((count) => this.unreadCountValue.set(count));
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollTimer = setInterval(() => {
      this.load();
    }, POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private mapNotification(item: NotificationApiItem): Notification {
    return {
      id: String(item.id),
      userId: String(item.user_id),
      type: item.type?.trim() || undefined,
      title: item.title,
      description: item.description,
      iconClass: item.icon_class,
      tone: item.tone,
      highlight: item.highlight?.trim() || undefined,
      highlightTone: item.highlight_tone ?? undefined,
      actionUrl: item.action_url?.trim() || undefined,
      read: item.read,
      dismissed: item.dismissed,
      dismissedAt: item.dismissed_at ?? undefined,
      createdAt: item.created_at,
    };
  }

  private mapPreference(
    item: NotificationPreferenceApiItem,
  ): NotificationPreference {
    return {
      notificationType: item.notification_type,
      muted: item.muted,
    };
  }

  private resolveErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'error' in error) {
      const body = (error as { error?: { message?: string } }).error;
      if (typeof body?.message === 'string' && body.message.trim() !== '') {
        return body.message;
      }
    }

    return 'Não foi possível carregar as notificações.';
  }

  private normalizePreferences(
    items: NotificationPreference[],
  ): NotificationPreference[] {
    const mutedByType = new Map(
      items.map((item) => [item.notificationType, item.muted]),
    );

    return defaultNotificationPreferences().map((item) => ({
      notificationType: item.notificationType,
      muted: mutedByType.get(item.notificationType) ?? false,
    }));
  }
}
